import { CollaborationPlugin as LexicalCollaborationPlugin } from '@lexical/react/LexicalCollaborationPlugin';
import type { LexicalEditor } from 'lexical';
import { useCallback, useRef } from 'react';

import { useEditorProps } from '../../core/context/EditorPropsContext';
import { useFeatureConfig } from '../../core/context/FeaturesContext';
import { $replaceContent } from '../../serialization/replaceContent';

/**
 * Binds the editor to a Yjs document so several clients can edit it at once.
 *
 * The transport is deliberately not owned here: the consumer passes a
 * `providerFactory`, so this lib stays free of any websocket/SignalR
 * dependency and the transport can be swapped without touching the editor.
 */
export const CollaborationPlugin = () => {
  const { namespace, initialValue, mode } = useEditorProps();
  const collaboration = useFeatureConfig('collaboration');

  // `initialValue` is a seed, not a binding. Freeze it: it is a dependency of
  // the bootstrap effect, so a consumer that echoes `onChange` back into the
  // prop would otherwise re-run it on every keystroke.
  const seed = useRef({ initialValue, mode }).current;

  /**
   * Only runs on the client that wins the bootstrap race, and only while the
   * shared document is still empty — every later client receives this content
   * over the wire instead.
   */
  const initialEditorState = useCallback((editor: LexicalEditor) => $replaceContent(editor, seed.initialValue, seed.mode), [seed]);

  if (!collaboration) return null;

  return (
    <LexicalCollaborationPlugin
      id={collaboration.room ?? namespace}
      providerFactory={collaboration.providerFactory}
      shouldBootstrap={collaboration.shouldBootstrap ?? true}
      initialEditorState={initialEditorState}
      username={collaboration.username}
      cursorColor={collaboration.cursorColor}
    />
  );
};
